import type { ReactNode } from "react";
import { useRecordContext, useTranslate } from "ra-core";
import { DateField } from "@/components/admin/date-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Link } from "react-router";

import type { Contact, Sale, Task } from "../types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskCategories } from "../root/defaultConfiguration";
import { PushToPlaneButton } from "./PushToPlaneButton";
import { CollaborationBadge } from "./CollaborationBadge";
import { DueCountdown } from "./DueCountdown";

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <div className="text-sm">{children}</div>
  </div>
);

// Read-only task detail shown when the task modal opens in view mode.
export const TaskDetailView = () => {
  const task = useRecordContext<Task>();
  const translate = useTranslate();
  const { taskTypes, taskCategories } = useConfigurationContext();
  if (!task) return null;

  const typeLabel =
    taskTypes.find((t) => t.value === task.type)?.label ?? task.type;
  const categoryLabel =
    (taskCategories ?? defaultTaskCategories).find(
      (c) => c.value === task.category,
    )?.label ?? task.category;

  return (
    <div className="flex flex-col gap-3">
      <Field label="Description">
        <p className="whitespace-pre-wrap">{task.text}</p>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Assignee">
          {task.sales_id ? (
            <ReferenceField<Task, Sale>
              source="sales_id"
              reference="sales"
              record={task}
              render={({ referenceRecord }) =>
                referenceRecord
                  ? `${referenceRecord.first_name} ${referenceRecord.last_name}`
                  : "—"
              }
            />
          ) : (
            "—"
          )}
        </Field>
        <Field label={translate("resources.tasks.fields.contact_id")}>
          {task.contact_id ? (
            <ReferenceField<Task, Contact>
              source="contact_id"
              reference="contacts"
              record={task}
              render={({ referenceRecord }) =>
                referenceRecord ? (
                  <Link
                    to={`/contacts/${task.contact_id}/show`}
                    className="underline underline-offset-2"
                  >
                    {`${referenceRecord.first_name} ${referenceRecord.last_name}`.trim()}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
          ) : (
            "—"
          )}
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Due date">
          <span className="inline-flex items-center gap-1.5">
            <DateField source="due_date" record={task} showDate showTime />
            {task.due_date ? (
              <>
                <span className="text-muted-foreground">·</span>
                <DueCountdown date={task.due_date} />
              </>
            ) : null}
          </span>
        </Field>
        <Field label="Type">
          {task.type && task.type !== "none" ? typeLabel : "—"}
        </Field>
        <Field label="Category">{categoryLabel}</Field>
      </div>
      <Field label="Status">{task.done_date ? "Done" : "Open"}</Field>
      {task.collaboration_id ? (
        <CollaborationBadge collaborationId={task.collaboration_id} />
      ) : null}
      <PushToPlaneButton task={task} />
    </div>
  );
};
